import React, { useEffect, useRef, useState } from 'react';
import GoogleFixedAds from '../googleAds/GoogleFixedads';
import './FullScreenAd.css';
import { GOOGLE_ADS_CLIENT } from '../../lib/config';

const FullScreenAd = ({ isOpen, onClose }) => {
  const [canClose, setCanClose] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Prevent closing immediately
      setCanClose(false);
      setCountdown(14);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanClose(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(timer);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Re-assert viewport pin if AdSense mutates overlay inline styles (height: auto !important)
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const overlay = overlayRef.current;
    if (!overlay) {
      return undefined;
    }

    const pinOverlayToViewport = () => {
      overlay.style.setProperty('position', 'fixed', 'important');
      overlay.style.setProperty('top', '0', 'important');
      overlay.style.setProperty('right', '0', 'important');
      overlay.style.setProperty('bottom', '0', 'important');
      overlay.style.setProperty('left', '0', 'important');
      overlay.style.setProperty('width', '100%', 'important');
      overlay.style.setProperty('min-height', '100vh', 'important');
    };

    pinOverlayToViewport();

    const observer = new MutationObserver(() => {
      pinOverlayToViewport();
    });
    observer.observe(overlay, { attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  const handleClose = () => {
    if (canClose) {
      // Track ad view
      if (window.gtag && window.cookieConsentGiven) {
        window.gtag('event', 'fullscreen_ad_closed', {
          event_category: 'Ad Engagement',
          event_label: 'Full Screen Ad Closed',
          value: 1
        });
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fullscreen-ad-overlay"
      onClick={canClose ? handleClose : undefined}
    >
      {/* Shell holds outside close button so card overflow does not clip or scroll */}
      <div className="fullscreen-ad-shell" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`fullscreen-ad-close ${canClose ? 'enabled' : 'disabled'}`}
          onClick={handleClose}
          disabled={!canClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="fullscreen-ad-container">
          <h2 className="fullscreen-ad-title">Preparing your download</h2>

          {/* Ad Content - slot 5433407966; sizing left to AdSense / prior layout */}
          <div className="fullscreen-ad-content">
            <GoogleFixedAds
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                minHeight: '250px'
              }}
              client={GOOGLE_ADS_CLIENT}
              slot="5433407966"
              format="auto"
              fullWidthResponsive={true}
            />
          </div>

          <div className="fullscreen-ad-footer">
            {!canClose ? (
              <>
                <div className="fullscreen-ad-countdown" aria-live="polite">
                  {countdown}
                </div>
                <p className="fullscreen-ad-wait-text">
                  Please wait a few moments for the download to begin
                </p>
              </>
            ) : (
              <button
                type="button"
                className="fullscreen-ad-skip"
                onClick={handleClose}
              >
                Continue to Video
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenAd;
